import Popover from '@mui/material/Popover'
import type { SxProps, Theme } from '@mui/material/styles'
import { useCallback, useId, useMemo, useRef, type FormEvent, type ReactNode } from 'react'
import { useIntl } from 'react-intl'

import { Button, cn } from '@/shared/ui'
import { focusRing, targetSize } from '@/shared/ui/cn'
import { X } from '@/shared/ui/icons'

export interface FilterPanelProps {
  readonly open: boolean
  readonly onClose: () => void
  readonly onClear: () => void
  /** The panel's heading; "Filter" by default. */
  readonly title?: string
  readonly children: ReactNode
}

// MUI's styles sit outside Tailwind's cascade layers and win over its classes, so the surface is
// styled here rather than by class: the mockup's page background, line outline and lifted shadow.
const paperSx: SxProps<Theme> = (theme) => ({
  backgroundColor: (theme.vars ?? theme).palette.background.default,
  backgroundImage: 'none',
  border: '1px solid',
  borderColor: (theme.vars ?? theme).palette.line,
  boxShadow: (theme.vars ?? theme).palette.elevation.cardHover,
  color: (theme.vars ?? theme).palette.text.primary,
  display: 'flex',
  flexDirection: 'column',
  mt: 1,
  overflow: 'hidden',
  width: '28rem',
})

const anchorOrigin = { vertical: 'bottom', horizontal: 'right' } as const
const transformOrigin = { vertical: 'top', horizontal: 'right' } as const

/**
 * The mockups' Filters popover (`components/find-search/FilterPanel.jsx`), opened under the button
 * that opens it, its right edge on the button's: wrap that button and this in one `relative`
 * element. Filtering is live, so Search, or Enter in any field, only closes it. Its height is capped
 * to the viewport, with the header and footer fixed and only the fields scrolling.
 *
 * It is a modal dialog (MUI's Popover): the page behind is hidden from assistive technology and
 * cannot take focus while it is open, Escape or a click outside closes it, and focus goes back to
 * the button. Where the button leaves too little room, such as at 320 px, the panel moves to stay
 * whole on screen, 16 px in from either edge.
 */
export function FilterPanel({ open, onClose, onClear, title, children }: FilterPanelProps) {
  const intl = useIntl()
  const anchor = useRef<HTMLSpanElement>(null)
  const headingId = useId()
  const anchorEl = useCallback(() => anchor.current ?? document.body, [])
  const slotProps = useMemo(
    () => ({ paper: { role: 'dialog', 'aria-modal': true, 'aria-labelledby': headingId, sx: paperSx } }),
    [headingId],
  )

  const submit = useCallback(
    (event: FormEvent) => {
      event.preventDefault()
      onClose()
    },
    [onClose],
  )

  const heading = title ?? intl.formatMessage({ id: 'search.filters.title' })
  const closeLabel = intl.formatMessage({ id: 'search.filters.close' })

  return (
    <>
      {/* Where the panel opens: the bottom right corner of the wrapper, under the button. */}
      <span ref={anchor} aria-hidden="true" className="pointer-events-none absolute bottom-0 right-0 h-0 w-0" />
      <Popover
        open={open}
        onClose={onClose}
        anchorEl={anchorEl}
        anchorOrigin={anchorOrigin}
        transformOrigin={transformOrigin}
        // It appears in place at once, as in the mockup.
        transitionDuration={0}
        slotProps={slotProps}
      >
        <form onSubmit={submit} className="flex max-h-[calc(100dvh-10rem)] min-h-0 flex-col">
          <div className="flex items-center justify-between gap-3 border-b border-divider px-4 py-3">
            <h2 id={headingId} className="font-heading text-base font-medium text-text-primary">
              {heading}
            </h2>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={onClear}
                className={cn(targetSize, 'cursor-pointer rounded px-1 text-xs text-primary-main hover:underline', focusRing)}
              >
                {intl.formatMessage({ id: 'search.filters.clearAll' })}
              </button>
              <button
                type="button"
                onClick={onClose}
                aria-label={closeLabel}
                className={cn(targetSize, 'cursor-pointer rounded text-text-secondary hover:text-text-primary', focusRing)}
              >
                <X size={16} />
              </button>
            </div>
          </div>

          <div className="flex min-h-0 flex-col gap-5 overflow-y-auto px-4 py-4">{children}</div>

          <div className="border-t border-divider px-4 py-3">
            <Button type="submit" className="w-full" size="small">
              {intl.formatMessage({ id: 'search.filters.search' })}
            </Button>
          </div>
        </form>
      </Popover>
    </>
  )
}
